import React, { useCallback, useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  debounce,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useActionHook } from "hooks";
import { useUserManageActions } from "store";
import { ICharContact } from "store/types";
import { IContact } from "store/types/user-management";

import { defaultContact } from ".";

interface Props {
  open: boolean;
  // The popup is anchored to the "Add Contact" button (Popover, no dimming backdrop).
  anchorEl: HTMLElement | null;
  onClose: () => void;
  // Ids already attached to the deal — shown as "Added" and locked.
  existingContactIds: Array<number | string>;
  onApply: (contacts: ICharContact[]) => void;
  onCreateNew: () => void;
}

const contactName = (contact: IContact): string =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim();

const contactInitial = (contact: IContact): string =>
  (contactName(contact) || contact.email || "").trim().charAt(0).toUpperCase() || "?";

// Map an account contact (ContactResource) onto the deal-characteristics contact shape.
const toCharContact = (contact: IContact): ICharContact => ({
  id: contact.id,
  first_name: contact.first_name ?? "",
  last_name: contact.last_name ?? "",
  email: contact.email ?? "",
  firm: contact.firm ?? "",
  title: contact.title ?? "",
  phone_number: contact.phone_number ?? "",
  address: contact.address ?? "",
  country: contact.country ?? null,
  referral: contact.referral ?? "",
  is_show: { ...defaultContact.is_show },
  role: null
});

const greenChipSx = {
  height: "24px",
  borderRadius: "20px",
  backgroundColor: "#98fcd9",
  color: "#071f35",
  fontSize: "12px",
  ".MuiChip-icon": { fontSize: "16px", color: "#071f35", marginLeft: "4px" }
} as const;

interface RowProps {
  contact: IContact;
  added: boolean;
  locked: boolean;
  onToggle: (id: number) => void;
}

const ContactRow: React.FC<RowProps> = ({ contact, added, locked, onToggle }) => (
  <Stack
    direction="row"
    sx={{
      alignItems: "center",
      gap: "8px",
      p: "8px",
      borderRadius: "8px",
      "&:hover": { backgroundColor: "#ecf1f8", ".add-action": { opacity: 1 } }
    }}
  >
    <Stack direction="row" sx={{ flex: "1 0 0", minWidth: 0, gap: "8px", alignItems: "center" }}>
      <Avatar sx={{ width: 40, height: 40, bgcolor: "#ede5f9", color: "#6e45eb", fontSize: "18px", fontWeight: 500 }}>
        {contactInitial(contact)}
      </Avatar>
      <Stack sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: "16px", fontWeight: 700, color: "#071f35" }}>
          {contactName(contact) || "Unnamed contact"}
        </Typography>
        {contact.email && (
          <Typography noWrap sx={{ fontSize: "14px", lineHeight: "16px", color: "#496682" }}>
            {contact.email}
          </Typography>
        )}
      </Stack>
    </Stack>

    {added ? (
      <Chip
        icon={<CheckIcon />}
        label="Added"
        onClick={locked ? undefined : () => onToggle(contact.id)}
        sx={{ ...greenChipSx, cursor: locked ? "default" : "pointer" }}
      />
    ) : (
      <Button
        className="add-action"
        variant="outlined"
        size="sm"
        startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
        onClick={() => onToggle(contact.id)}
        sx={{
          opacity: 0,
          borderColor: "#071f35",
          color: "#071f35",
          borderRadius: "32px",
          textTransform: "none"
        }}
      >
        Add
      </Button>
    )}
  </Stack>
);

// Picker over the account's contacts (GET /contacts). Selecting rows and pressing Apply attaches the
// chosen contacts to the deal. Opened from the "Add Contact" button in the characteristics Contact tab.
export const AddContactPopup: React.FC<Props> = ({
  open,
  anchorEl,
  onClose,
  existingContactIds,
  onApply,
  onCreateNew
}) => {
  const { getContactsAction } = useUserManageActions();
  const { data, loading, action } = useActionHook(getContactsAction);

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const existingSet = useMemo(() => new Set(existingContactIds), [existingContactIds]);

  // Reset transient state each time the popup opens and load the first page.
  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedIds([]);
      action({ page: 1, per_page: 50 });
    }
  }, [open, action]);

  const runSearch = useCallback((value: string) => action({ search: value, page: 1, per_page: 50 }), [action]);
  const runSearchDebounced = useMemo(() => debounce(runSearch, 500), [runSearch]);
  useEffect(() => () => runSearchDebounced.clear(), [runSearchDebounced]);

  const onSearchChange = (value: string) => {
    setSearch(value);
    runSearchDebounced(value);
  };

  const toggle = useCallback((id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const rows = data?.data ?? [];

  const handleApply = () => {
    const chosen = rows.filter((contact) => selectedIds.includes(contact.id)).map(toCharContact);
    onApply(chosen);
    onClose();
  };

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            width: "520px",
            maxWidth: "520px",
            p: "24px",
            gap: "20px",
            borderRadius: "8px",
            boxShadow: "0px 10px 15px -3px rgba(18,18,23,0.08), 0px 4px 6px -2px rgba(18,18,23,0.05)"
          }
        }
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: "6px", pb: "8px" }}>
        <Typography sx={{ flex: "1 0 0", fontSize: "18px", color: "#071f35" }}>Add Contact</Typography>
        <IconButton onClick={onClose} sx={{ p: "4px" }} aria-label="close">
          <CloseIcon sx={{ fontSize: "16px", color: "#071f35" }} />
        </IconButton>
      </Stack>

      <Stack sx={{ gap: "10px" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#92a7bc" }} />
              </InputAdornment>
            )
          }}
        />

        <Stack sx={{ height: "368px", overflowY: "auto", borderBottom: "1px solid #dbe8f5" }}>
          {loading ? (
            <Stack sx={{ flexGrow: 1 }} alignItems="center" justifyContent="center">
              <CircularProgress size={32} />
            </Stack>
          ) : rows.length === 0 ? (
            <Stack sx={{ flexGrow: 1 }} alignItems="center" justifyContent="center">
              <Typography sx={{ fontSize: "14px", color: "#898989" }}>No contacts found.</Typography>
            </Stack>
          ) : (
            rows.map((contact) => {
              const locked = existingSet.has(contact.id);
              return (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  added={locked || selectedIds.includes(contact.id)}
                  locked={locked}
                  onToggle={toggle}
                />
              );
            })
          )}
        </Stack>

        <Stack direction="row" sx={{ py: "6px" }}>
          <Button
            variant="text"
            onClick={onCreateNew}
            startIcon={<PersonOutlineIcon sx={{ fontSize: "16px" }} />}
            sx={{ background: "#f0f0f0", color: "#071f35", padding: "4px 10px", textTransform: "none" }}
          >
            <Typography fontSize="12px" sx={{ color: "#071f35" }}>
              Create New Contact
            </Typography>
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ alignItems: "center", gap: "8px" }}>
        <Typography sx={{ flex: "1 0 0", fontSize: "14px", color: "#496682" }}>
          {selectedIds.length > 0 ? `${selectedIds.length} Contact${selectedIds.length > 1 ? "s" : ""} selected` : ""}
        </Typography>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: "32px", textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={selectedIds.length === 0}
          sx={{ borderRadius: "32px", textTransform: "none", bgcolor: "#071f35", "&:hover": { bgcolor: "#0a3142" } }}
        >
          Apply
        </Button>
      </Stack>
    </Popover>
  );
};
